import React, { useState } from 'react';
import { useAddEndorsementMutation } from '../../../../../../store/Company/companyApiSlice';
import { useSelector } from "react-redux";
import { selectActiveCompanyId } from '../../../../../../store/activeCompanySlice';
import { CommonToast } from '../../../../../common/Toast';
import { useGetUserQuery } from '../../../../../../store/auth/usersApiSlice';
import { ReactComponent as ProfileImage } from "../../../../../../assets/images/profile.svg";
import { convertLetterCase, getCountryByCode } from '../../../../../../utils/Helper';
import { Select } from '../../../../../common';

const EndorsementForm = ({ onClose, company, trevexId }) => {

    const { token, userInfo } = useSelector((state) => state.auth);
    // TIP-487: the company the user is currently acting as (the endorsing company).
    const activeCompanyId = useSelector(selectActiveCompanyId);

    const [businessRole, setBusinessRole] = useState('');
    const [durationOfBusiness, setDurationOfBusiness] = useState('');
    const [lastBusiness, setLastBusiness] = useState('');
    const [addEndorsement, { isLoading, isSuccess, isError, error }] = useAddEndorsementMutation();
    const { refetch } = useGetUserQuery(token);

    const [errors, setErrors] = useState({});

    const companyId = company?.companyID;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (trevexId) {
            try {
                const endorsementData = {
                    business_role: businessRole,
                    duration_of_business: durationOfBusiness,
                    last_business: lastBusiness,
                    individualID: userInfo?.individual[0]?.individualID,
                    // TIP-487: which of the user's companies is giving this endorsement.
                    acting_company_id: activeCompanyId,
                };

                const response = await addEndorsement({ companyId, endorsementData, token }).unwrap();
                if (response) {
                    refetch();
                    CommonToast.success('Endoresment added successfully');
                }
                onClose(); // Close the form if submission is successful
            } catch (err) {
                const errorData = err?.data || {}; // Here you need to adapt the path to your actual error response format
                setErrors(errorData); // Set errors for displaying
                console.error('Failed to add endorsement', errorData);
            }
        } else {
            CommonToast.error('Invalid Endorsement')
        }
    };

    const isCustomerSelected = businessRole === 'customer';
    const disabledPlaceholder = "Customers cannot endorse a business.";

    return (
        <div className="fixed inset-0 overflow-y-auto h-full w-full z-40">
            <div className="relative top-20 mx-auto p-5 border w-full sm:w-[34rem] shadow-lg rounded-md bg-white">
                <div className="flex justify-between items-start">
                    <h5 className="text-lg text-secondarytwo mb-4 text-left">Add Endorsement</h5>
                    <button onClick={onClose} className="cursor-pointer">
                        <span className="text-2xl text-secondary font-bold">&times;</span>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 text-left">
                    <div>
                        <p className='block text-sm font-semibold text-gray-700 text-left'>Business you are endorsing</p>
                        <div className='flex text-black gap-2 mt-1'>
                            <div>
                                {!company?.logo ? (
                                    <ProfileImage className='w-[40px] h-[40px]' />
                                ) : (
                                    <>
                                        <img
                                            className="w-[40px] h-[40px]"
                                            src={company?.logo}
                                            alt="Business Logo"
                                        />
                                    </>
                                )}
                            </div>
                            <div>
                                <p className='text-f14 font-normal'>{convertLetterCase(trevexId
                                    ? company?.registered_company_name
                                    : company?.company?.name, "titlecase")}</p>
                                <p>
                                    <p className="text-f12 countrystate font-normal">
                                        <span>
                                            {company?.claimed && trevexId &&
                                                company?.companyAddress[0]?.company_address_detailsID?.state && company?.companyAddress[0]?.company_address_detailsID?.state != "" &&
                                                `${company?.companyAddress[0]?.company_address_detailsID?.state.toLowerCase() + ", "} `}
                                        </span>
                                        {
                                            company?.state ?
                                                <span>{company.state.toLowerCase()}, </span> :
                                                <span>
                                                    {
                                                        !trevexId && company?.company?.city !== '' && `${company?.company?.city.toLowerCase()}, `
                                                    }
                                                </span>
                                        }
                                        <span>{trevexId && getCountryByCode(company?.country_code).toLowerCase()}
                                            {!trevexId && company?.company?.country.toLowerCase()}</span>
                                    </p>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="businessRole" className="block text-sm font-semibold text-gray-700 text-left">Business Role</label> {/* Explicitly add text-left */}
                        <Select
                            id="businessRole"
                            value={businessRole}
                            onChange={(e) => setBusinessRole(e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded shadow-sm text-black"
                        >
                            <option value="">Select Role</option>
                            <option value="customer">I am a customer of this business</option>
                            <option value="supplier">I am a supplier of this business</option>
                            <option value="service_provider">I am a service provider of this business</option>
                        </Select>
                    </div>
                    <div>
                        <label htmlFor="years_in_business" className="block text-sm font-semibold text-gray-700 text-left">Years in Business</label>
                        {isCustomerSelected ? (
                            <input
                                type="number"
                                id="years_in_business"
                                value={durationOfBusiness}
                                onChange={(e) => setDurationOfBusiness(e.target.value)}
                                disabled={isCustomerSelected}
                                placeholder={isCustomerSelected ? disabledPlaceholder : "Enter years"}
                                className="mt-1 block w-full p-2 border border-gray-300 rounded shadow-sm text-black"
                            />
                        ) : (
                            <Select
                                id="years_in_business"
                                value={durationOfBusiness}
                                onChange={(e) => setDurationOfBusiness(e.target.value)}
                                className="mt-1 block w-full p-2 border border-gray-300 rounded shadow-sm text-black"
                                disable={isCustomerSelected} // This will disable the select when isCustomerSelected is true
                            >
                                <option value="">Select Year In Business</option>
                                <option value="<1">Less than 1 year</option>
                                <option value="1-3">1-3 years</option>
                                <option value="3-5">3-5 years</option> {/* Corrected the range */}
                                <option value="5-10">5-10 years</option>
                                <option value="10+">10+ years</option>
                            </Select>
                        )}
                    </div>
                    <div>
                        <label htmlFor="lastBusiness" className="block text-sm font-semibold text-gray-700 text-left">Last Business (Month/Year)</label> {/* Explicitly add text-left */}
                        <input
                            type="month"
                            id="lastBusiness"
                            value={lastBusiness}
                            onChange={(e) => setLastBusiness(e.target.value)}
                            disabled={isCustomerSelected}
                            placeholder={isCustomerSelected ? disabledPlaceholder : "Select month and year"}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded shadow-sm text-black"
                        />
                    </div>
                    <button
                        type="submit"
                        className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 text-sm font-medium shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-dark"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Loading...' : 'Submit'}
                    </button>
                    {Object.keys(errors).length > 0 && (
                        <div className="text-red-500 mt-2">
                            {Object.entries(errors).map(([key, value]) => (
                                <p key={key}>{value}</p>
                            ))}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default EndorsementForm;
